import { Grid2x2, List } from 'lucide-react';
import type { FilesViewMode } from '../../../lib/files-url-state';
import { useTranslation } from '../../../lib/i18n';
import { IconButton } from '../ui';
import styles from './FilesViewToggle.module.css';

// List or thumbnails. Two states, so it is two glyphs rather than a picker: the one you are in reads as pressed.
//
// The glyphs are Figma's own layer names: **`16/grid-2x2`** and **`16/list`**. That is lucide `Grid2x2` —
// a square divided by a cross into four cells — NOT `LayoutGrid`, which draws four separate filled
// blocks. The two are near-indistinguishable at 16px, which is exactly why the design names them and why
// this comment does too: we shipped `LayoutGrid`.
//
// The choice is PERSISTED in the URL with the rest of the browser state, which makes it survive a refresh and
// travel in a shared link — a person who prefers thumbnails prefers them on the second visit too. It is
// deliberately NOT reset by "Clear filters": how you like to look at files is a preference, not part of the
// question you asked of them.
export function FilesViewToggle({
  view,
  onChange,
  className,
}: {
  view: FilesViewMode;
  onChange: (view: FilesViewMode) => void;
  /** Layout from the enclosing row — the switch owns its two glyphs, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={className ? `${styles.toggle} ${className}` : styles.toggle}
      role="group"
      aria-label={t('files.view.label')}
    >
      {/* GRID first, list second — the order the WIDE header draws them (Figma `15623:362406`). It used
          to be list-then-grid.

          ⚠️ THE PHONE DRAWS THE OPPOSITE ORDER — list, then grid (`15891:421734`), where the pair sits on
          the breadcrumb's own row. Both nodes were checked; the divergence is what the design says, not a
          bug in one of them, so the DOM keeps the wide order and the stylesheet reverses the pair inside
          the one container query this screen folds on. */}
      <IconButton
        size="medium"
        aria-label={t('files.view.thumbnails')}
        aria-pressed={view === 'thumbnails'}
        data-active={view === 'thumbnails' || undefined}
        onClick={() => onChange('thumbnails')}
      >
        <Grid2x2 size={16} aria-hidden />
      </IconButton>
      <IconButton
        size="medium"
        aria-label={t('files.view.list')}
        aria-pressed={view === 'list'}
        data-active={view === 'list' || undefined}
        onClick={() => onChange('list')}
      >
        <List size={16} aria-hidden />
      </IconButton>
    </div>
  );
}
